import type { ChallengeData } from "./api"

import { isBoolean, isNumber, isObject, isString } from "../modules/utils/utils"

export interface BattleStorage {
  clearBattle: ()=> void
  loadBattle: ()=> null | StoredBattle
  saveBattle: (input: StoredBattle)=> void
}

export interface StoredBattle {
  challenge: ChallengeData
  hintsUsed?: number
  lost: boolean
  paused?: boolean
  pausedAt?: number
  startedAt: number
  undoCount?: number
}

export function createBattleStorage(storageKey: string): BattleStorage {
  return {
    clearBattle(): void {
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        /* ignore */
      }
    },
    loadBattle(): null | StoredBattle {
      try {
        const stored = sessionStorage.getItem(storageKey)
        if (!stored) {
          return null
        }
        // SAFETY: stored as string, parsed from JSON
        const parsed = JSON.parse(stored) as Partial<StoredBattle>
        const challenge = parseChallengeData(parsed.challenge)
        if (challenge === null || !isNumber(parsed.startedAt)) {
          return null
        }
        return {
          challenge,
          hintsUsed: parsed.hintsUsed,
          lost: parsed.lost === true,
          paused: parsed.paused === true,
          pausedAt: parsed.pausedAt,
          startedAt: parsed.startedAt,
          undoCount: parsed.undoCount,
        }
      } catch {
        return null
      }
    },
    saveBattle(input: StoredBattle): void {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(input))
      } catch {
        /* sessionStorage unavailable — battle resumes only in-memory */
      }
    },
  }
}

function parseChallengeData(raw: unknown): ChallengeData | null {
  // SAFETY: boundary input from JSON.parse, shape validated below
  if (!isObject(raw)) {
    return null
  }
  const parsed = raw as ChallengeData
  if (
    isString(parsed.created_by) &&
    isBoolean(parsed.hard_mode) &&
    isNumber(parsed.hints_used) &&
    isNumber(parsed.id) &&
    isNumber(parsed.index) &&
    (parsed.time === null || isNumber(parsed.time))
  ) {
    return parsed
  }
  return null
}

export const acceptFlowStorage = createBattleStorage(
  "sortaballs-challenge-battle",
)
export const createFlowStorage = createBattleStorage("sortaballs-create-battle")
